
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getDisplayName } from '../utils/nameUtils';

const API_BASE = process.env.REACT_APP_API_BASE;

function MemberModal({ member, isOpen, onClose }) {
    const [memberDetails, setMemberDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);



    const loadMemberDetails = useCallback(async () => {
        if (!member) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.get(`${API_BASE}/api/member-details/${member.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMemberDetails(response.data);
        } catch (err) {
            console.error('Fehler beim Laden der Member-Details:', err.message);
            setError('Member-Details konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    }, [member]);

    useEffect(() => {
        if (isOpen && member) {
            loadMemberDetails();
        }
    }, [isOpen, member, loadMemberDetails]);

    const formatDate = (dateString) => {
        if (!dateString) return 'Unbekannt';
        return new Date(dateString).toLocaleDateString('de-DE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusIcon = (status) => {
        const icons = {
            online: '🟢',
            idle: '🟡', 
            dnd: '🔴',
            offline: '⚫'
        };
        return icons[status] || '⚫';
    };

    const getActivityIcon = (type) => {
        const icons = {
            0: '🎮', // Playing
            1: '📺', // Streaming  
            2: '🎵', // Listening
            3: '📺', // Watching
            4: '📝', // Custom
            5: '🏆'  // Competing
        };
        return icons[type] || '🎮';
    };



    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Member Details</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                
                <div className="modal-body">
                    {loading && <p className="muted">Lädt Member-Details...</p>}
                    
                    {error && (
                        <div className="error-message">
                            <p>{error}</p>
                            <button onClick={loadMemberDetails}>Erneut versuchen</button>
                        </div>
                    )}
                    
                    {memberDetails && (
                        <div className="member-details">
                            {/* Header mit Avatar und Grundinfo */}
                            <div className="member-header">
                                <div className="member-avatar">
                                    <img 
                                        src={memberDetails.avatarURL || `https://cdn.discordapp.com/embed/avatars/${memberDetails.discriminator % 5}.png`}
                                        alt="Avatar"
                                        width="80"
                                        height="80"
                                        style={{ borderRadius: '50%' }}
                                    />
                                </div>
                                <div className="member-info">
                                    <h2 style={{ margin: '0 0 5px 0' }}>
                                        {getDisplayName(memberDetails)}
                                    </h2>
                                    <p className="muted">
                                        {memberDetails.username}#{memberDetails.discriminator}
                                    </p>
                                    <p>
                                        <span className="status-indicator">
                                            {getStatusIcon(memberDetails.status)} {memberDetails.status.toUpperCase()}
                                        </span>
                                    </p>
                                </div>
                            </div>

                            {/* Detaillierte Informationen */}
                            <div className="member-stats" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', margin: '15px 0' }}>
                                <div className="stat-item">
                                    <strong>Discord ID:</strong><br/>
                                    <code>{memberDetails.id}</code>
                                </div>
                                <div className="stat-item">
                                    <strong>Status:</strong><br/>
                                    <span style={{ color: memberDetails.status === 'online' ? '#4CAF50' : '#FF9800' }}>
                                        {memberDetails.status.toUpperCase()}
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <strong>Team beigetreten:</strong><br/>
                                    {formatDate(memberDetails.teamJoinDate)}
                                </div>
                                <div className="stat-item">
                                    <strong>Server beigetreten:</strong><br/>
                                    {formatDate(memberDetails.joinedAt)}
                                </div>
                                {memberDetails.in_voice && (
                                    <div className="stat-item">
                                        <strong>Voice Channel:</strong><br/>
                                        🎧 {memberDetails.voice_channel_name}
                                    </div>
                                )}
                                <div className="stat-item">
                                    <strong>Permission Level:</strong><br/>
                                    {memberDetails.permissions.length} Berechtigungen
                                </div>
                            </div>

                            {/* Aktivitäten */}
                            {memberDetails.activity && memberDetails.activity.length > 0 && (
                                <div className="member-activities" style={{ margin: '15px 0' }}>
                                    <h4>Aktuelle Aktivitäten</h4>
                                    {memberDetails.activity.map((activity, index) => (
                                        <div key={index} className="activity-item" style={{ 
                                            padding: '8px', 
                                            margin: '5px 0', 
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: '4px'
                                        }}>
                                            <span style={{ marginRight: '8px' }}>{getActivityIcon(activity.type)}</span>
                                            <strong>{activity.name}</strong>
                                            {activity.details && <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>{activity.details}</p>}
                                            {activity.state && <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>{activity.state}</p>}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Rollen */}
                            <div className="member-roles" style={{ margin: '15px 0' }}>
                                <h4>Rollen ({memberDetails.roles.length})</h4>
                                <div className="roles-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                    {memberDetails.roles.map(role => (
                                        <span
                                            key={role.id}
                                            className="role-badge"
                                            style={{
                                                padding: '4px 8px',
                                                borderRadius: '12px',
                                                backgroundColor: role.color !== '#000000' ? role.color + '40' : '#333',
                                                color: role.color !== '#000000' ? role.color : '#fff',
                                                border: `1px solid ${role.color !== '#000000' ? role.color : '#333'}`,
                                                fontSize: '0.85em'
                                            }}
                                        >
                                            {role.name}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Ban-Status */}
                            {memberDetails.isBanned && (
                                <div className="ban-info" style={{ 
                                    padding: '10px', 
                                    backgroundColor: 'rgba(255, 0, 0, 0.1)', 
                                    border: '1px solid #ff4444',
                                    borderRadius: '5px',
                                    margin: '15px 0'
                                }}>
                                    <h4 style={{ color: '#ff4444', margin: '0 0 5px 0' }}>🚫 Gebannt</h4>
                                    <p><strong>Grund:</strong> {memberDetails.banReason}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Modal Styles */}
            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.8);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                }
                
                .modal-content {
                    background: #1a1a1a;
                    border: 1px solid #333;
                    border-radius: 10px;
                    width: 90%;
                    max-width: 600px;
                    max-height: 80vh;
                    overflow-y: auto;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
                }
                
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px 20px;
                    border-bottom: 1px solid #333;
                    background-color: rgba(255, 255, 255, 0.05);
                }
                
                .modal-close {
                    background: none;
                    border: none;
                    color: #999;
                    font-size: 24px;
                    cursor: pointer;
                    padding: 0;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .modal-close:hover {
                    color: #fff;
                }
                
                .modal-body {
                    padding: 20px;
                }
                
                .member-header {
                    display: flex;
                    gap: 15px;
                    margin-bottom: 20px;
                    padding-bottom: 15px;
                    border-bottom: 1px solid #333;
                }
                
                .error-message {
                    background-color: rgba(255, 0, 0, 0.1);
                    border: 1px solid #ff4444;
                    color: #ff4444;
                    padding: 15px;
                    border-radius: 5px;
                    text-align: center;
                }
                
                .error-message button {
                    margin-top: 10px;
                    padding: 5px 15px;
                    background-color: #ff4444;
                    color: white;
                    border: none;
                    border-radius: 3px;
                    cursor: pointer;
                }
                
                .status-indicator {
                    padding: 2px 8px;
                    border-radius: 10px;
                    background-color: rgba(255, 255, 255, 0.1);
                    font-size: 0.9em;
                }
            `}</style>
        </div>
    );
}

export default MemberModal;
